import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftRight, ShieldCheck, Snowflake, Wallet, QrCode, CheckCircle2, Code2, Languages } from 'lucide-react';
import SwapForm from './SwapForm';
import SplitNowSwap from './SplitNowSwap';
import { useLanguage } from '../i18n/LanguageContext';
import '../styles/App.css';

// ── Ice crystal fracture paths (SVG polylines) ────────────────────
// Each path is a series of connected line segments radiating from
// different origin points, simulating how ice cracks under pressure.
const ICE_PATHS = [
  // Large central fracture system
  'M 50 20 L 48 35 L 44 48 L 38 60 L 30 75',
  'M 50 20 L 52 38 L 58 52 L 65 62 L 72 78',
  'M 50 20 L 46 30 L 55 42 L 60 58',
  'M 38 60 L 28 65 L 18 72',
  'M 65 62 L 75 68 L 85 65 L 92 70',
  // Upper-right cluster
  'M 80 10 L 82 22 L 85 35 L 88 48',
  'M 80 10 L 75 24 L 70 38 L 68 50',
  'M 88 48 L 92 55 L 96 62',
  // Lower-left fractures
  'M 15 60 L 20 72 L 25 82 L 22 92',
  'M 15 60 L 10 70 L 8 82',
  // Wide branching fractures
  'M 30 5 L 35 18 L 32 30 L 28 44',
  'M 70 5 L 68 20 L 72 32 L 78 44',
  // Horizontal stress lines
  'M 0 45 L 15 48 L 28 44 L 38 48 L 50 45',
  'M 100 55 L 85 52 L 72 55 L 60 52 L 50 55',
];

const IceCrystalBackground = () => (
  <div className="ice-bg" aria-hidden="true">
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
    >
      {ICE_PATHS.map((d, i) => (
        <polyline
          key={i}
          className="ice-fracture"
          points={d.replace(/[ML] /g, '').replace(/[ML]/g, '')}
          style={{ animationDelay: `${i * 0.18}s` }}
        />
      ))}
      {/* Faint hexagonal crystal nodes at fracture origins */}
      {[
        [50, 20], [80, 10], [15, 60], [30, 5], [70, 5],
      ].map(([cx, cy], i) => (
        <circle
          key={`node-${i}`}
          cx={cx} cy={cy} r="0.8"
          fill="rgba(125,211,252,0.6)"
          style={{
            animation: `fracture-draw 0.3s ease forwards`,
            animationDelay: `${i * 0.18}s`,
            opacity: 0,
          }}
        />
      ))}
    </svg>
  </div>
);

const SwapRouter = ({ theme, toggleTheme }) => {
  const [mode, setMode] = useState('any');
  const { t, lang, setLang, languages } = useLanguage();

  return (
    <div className="page">
      {/* ── Ice background ── */}
      <IceCrystalBackground />
      <div className="ice-glow" aria-hidden="true" />

      {/* ── Header ── */}
      <header className="site-header">
        <a className="brand" href="/">
          <span className="brand-mark">
            <Snowflake size={18} strokeWidth={2.5} />
          </span>
          <span className="brand-name">
            Ice<span>Swap</span>
          </span>
        </a>
        <div className="header-actions">
          <div className="lang-select-wrap">
            <Languages size={13} />
            <select
              className="lang-select"
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              aria-label={t('lang.selectAria')}
            >
              {languages.map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
          </div>
        </div>
      </header>

      {/* ── Main ── */}
      <main className="home">
        <div className="hero">
          <h1>
            Swap any coin,<br />
            <em>instantly frozen</em> to the best rate.
          </h1>
          <p>Non-custodial. No account. No trace.</p>
        </div>

        {/* ── Mode tabs ── */}
        <div className="mode-toggle" role="tablist" aria-label="Swap mode">
          <button
            role="tab"
            aria-selected={mode === 'any'}
            className={`mode-btn blue ${mode === 'any' ? 'active' : ''}`}
            onClick={() => setMode('any')}
          >
            <span className="mode-icon"><ArrowLeftRight size={15} strokeWidth={2.5} /></span>
            <span className="mode-label">{t('home.mode.any.label')}</span>
            <span className="mode-sub">{t('home.mode.any.sub')}</span>
          </button>
          <button
            role="tab"
            aria-selected={mode === 'private'}
            className={`mode-btn thaw ${mode === 'private' ? 'active' : ''}`}
            onClick={() => setMode('private')}
          >
            <span className="mode-icon"><ShieldCheck size={15} strokeWidth={2.5} /></span>
            <span className="mode-label">{t('home.mode.private.label')}</span>
            <span className="mode-sub">{t('home.mode.private.sub')}</span>
          </button>
        </div>

        <p className="mode-desc">
          {mode === 'any' ? t('home.mode.any.desc') : t('home.mode.private.desc')}
        </p>

        {/* ── Swap form ── */}
        <div className="swap-content">
          {mode === 'any' ? <SwapForm /> : <SplitNowSwap />}
        </div>

        {/* ── How it works ── */}
        <section className="how-it-works" aria-label={t('home.how.title')}>
          <h2>{t('home.how.title')}</h2>
          <div className="steps">
            <div className="step">
              <span className="step-num">1</span>
              <div className="step-body">
                <h3>{t('home.how.step1.title')}</h3>
                <p>{t('home.how.step1.desc')}</p>
              </div>
            </div>
            <div className="step">
              <span className="step-num">2</span>
              <div className="step-body">
                <h3>{t('home.how.step2.title')}</h3>
                <p>{t('home.how.step2.desc')}</p>
              </div>
            </div>
            <div className="step">
              <span className="step-num">3</span>
              <div className="step-body">
                <h3>{t('home.how.step3.title')}</h3>
                <p>{t('home.how.step3.desc')}</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* ── Footer ── */}
      <footer className="site-footer">
        <div className="trust-strip">
          <span><Wallet size={13} /> {t('home.footer.noAccount')}</span>
          <span><QrCode size={13} /> {t('home.footer.nonCustodial')}</span>
          <span><CheckCircle2 size={13} /> {t('home.footer.bestRate')}</span>
        </div>
        <p className="footer-fineprint">{t('home.footer.fineprint')}</p>
        <Link
          to="/docs"
          className="header-link"
          style={{ marginTop: '0.9rem', display: 'inline-flex' }}
        >
          <Code2 size={13} /> {t('home.footer.apiDocs')}
        </Link>
      </footer>
    </div>
  );
};

export default SwapRouter;
